// PDF download / share UI (task 6.2) — Req 8.3.
//
// The Phase 6 backend (task 6.2) exposes:
//   GET /quotes/{id}/pdf     → { type:'quote',   url, expiresIn, contentType, filename }
//   GET /invoices/{id}/pdf   → { type:'invoice', url, expiresIn, contentType, filename }
//
// Each call (re)renders the finalised document to the PRIVATE docs bucket and returns
// a SHORT-lived presigned GET URL. The bucket is never public, so the URL is the only
// way to read the PDF and it expires within a couple of minutes — which is why we
// fetch it on demand, right when the electrician taps download/share, rather than
// caching it.
//
// This module holds the one reusable card the quote and invoice detail screens both
// mount, so the pattern is identical in both places (mirrors quickbooksPushCard).
//
// Mobile behaviour (Req 8.3 — "download or share ... from a mobile device"):
//   • Download — fetch the presigned URL, then click a hidden <a> pointing at it.
//     The presigned URL carries Content-Disposition: attachment, so the phone saves
//     the file with a sensible name (opening a new tab after an await is blocked by
//     iOS pop-up rules, so an anchor click is the reliable path).
//   • Share — only offered where the Web Share API can share files. It fetches the
//     PDF as a Blob and hands it to the native share sheet (Messages, Mail, WhatsApp,
//     AirDrop…); if the platform can't share that file it falls back to download.

import { api } from '../api.js';
import {
  h, mount, card, muted,
  button, buttonRow, errorSlot, setError, runAction,
} from './ui.js';

// Can this browser share a File via the native share sheet? Feature-detected so the
// Share button only appears where it actually works (most desktop browsers can't).
export function canShareFiles() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function'
  );
}

// Trigger a download of the presigned URL via a hidden anchor click. Works from an
// async handler (unlike window.open, which mobile pop-up blockers refuse).
function triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Fetch the PDF bytes and offer them to the native share sheet. Falls back to a plain
// download if the platform can't share this file. Returns nothing; throws on a real
// failure (a user cancelling the share sheet is swallowed as a non-error).
async function shareFile(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch the PDF (${res.status})`);
  const blob = await res.blob();
  const file = new File([blob], filename || 'document.pdf', { type: 'application/pdf' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename || 'Document' });
    } catch (err) {
      // The user dismissing the share sheet rejects with AbortError — not an error
      // worth surfacing. Anything else propagates.
      if (!err || err.name !== 'AbortError') throw err;
    }
    return;
  }
  triggerDownload(url, filename);
}

// The download/share card for a quote or invoice detail screen. `entity` is 'quote'
// or 'invoice'; `doc` is the loaded record. Each button fetches a fresh presigned URL
// on click (they expire quickly) and shows loading/error state inline.
export function pdfDownloadCard({ entity, doc } = {}) {
  const path = `/${entity}s/${encodeURIComponent(doc.id)}/pdf`;
  const err = errorSlot();

  const downloadBtn = button('⬇ Download PDF', { variant: 'primary' });
  downloadBtn.addEventListener('click', async () => {
    const res = await runAction(downloadBtn, () => api.get(path), {
      busyLabel: 'Preparing…',
      errSlot: err,
    });
    if (res && res.url) triggerDownload(res.url, res.filename);
  });

  const controls = [downloadBtn];

  if (canShareFiles()) {
    const shareBtn = button('Share PDF', {});
    shareBtn.addEventListener('click', async () => {
      const original = shareBtn.textContent;
      shareBtn.disabled = true;
      shareBtn.textContent = 'Preparing…';
      setError(err, '');
      try {
        const res = await api.get(path);
        if (res && res.url) await shareFile(res.url, res.filename);
      } catch (e) {
        setError(err, (e && e.message) || 'Could not share the PDF. Please try again.');
      }
      shareBtn.disabled = false;
      shareBtn.textContent = original;
    });
    controls.push(shareBtn);
  }

  return h('div', {}, [
    card([
      muted(`Generate a PDF of this ${entity} to download or share. The link is short-lived, so it’s created fresh each time you tap.`),
      buttonRow(controls),
      err,
    ], { title: 'PDF document' }),
  ]);
}
